import { computed, ref } from 'vue'

export type NotificationType = 'warning' | 'success' | 'error' | 'info'

export type NotificationInput = {
  type: NotificationType
  title: string
  description?: string
  dismissable?: boolean
  timeoutMs?: number
}

export type AppNotification = NotificationInput & {
  id: string
  dismissable: boolean
}

const DEFAULT_TIMEOUT_MS = 5000

const notifications = ref<AppNotification[]>([])
const visibleNotifications = computed(() => notifications.value)
const notificationTimeouts = new Map<string, number>()

const createNotificationId = () => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID()
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2)}`
}

const clearNotificationTimeout = (id: string) => {
  const timeoutId = notificationTimeouts.get(id)
  if (timeoutId) {
    clearTimeout(timeoutId)
    notificationTimeouts.delete(id)
  }
}

export const dismissNotification = (id: string) => {
  clearNotificationTimeout(id)
  notifications.value = notifications.value.filter((notification) => notification.id !== id)
}

export const notify = (notification: NotificationInput) => {
  const id = createNotificationId()
  const appNotification: AppNotification = {
    ...notification,
    id,
    dismissable: notification.dismissable ?? false,
  }

  notifications.value = [...notifications.value, appNotification]

  if (!appNotification.dismissable) {
    const timeoutId = window.setTimeout(() => {
      dismissNotification(id)
    }, notification.timeoutMs ?? DEFAULT_TIMEOUT_MS)

    notificationTimeouts.set(id, timeoutId)
  }

  return id
}

export const useNotifications = () => {
  return {
    notifications: visibleNotifications,
    notify,
    dismissNotification,
  }
}
